import { useCallback, useEffect, useState } from "react";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";
import { Ban, CalendarPlus, Clock, Loader2, Plus, Trash2, X } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
    adminAvailability,
    adminSaveSchedule,
    adminAddOpening,
    adminDeleteOpening,
    adminBlocks,
    adminAddBlock,
    adminDeleteBlock,
} from "../../lib/api";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const fmtTime = (t) => {
    const [h, m] = t.split(":").map(Number);
    const ampm = h >= 12 ? "PM" : "AM";
    const hr = h % 12 === 0 ? 12 : h % 12;
    return `${hr}:${String(m).padStart(2, "0")} ${ampm}`;
};

const fmtDate = (d) => {
    try {
        return format(parseISO(d), "EEE, MMM d");
    } catch {
        return d;
    }
};

export const Availability = ({ token }) => {
    const [days, setDays] = useState(null);
    const [openings, setOpenings] = useState([]);
    const [blocks, setBlocks] = useState([]);
    const [timeInputs, setTimeInputs] = useState({});
    const [openingForm, setOpeningForm] = useState({ date: "", time: "" });
    const [blockForm, setBlockForm] = useState({ date: "", time: "" });
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        try {
            const [av, bl] = await Promise.all([adminAvailability(token), adminBlocks(token)]);
            setDays(av.schedule);
            setOpenings(av.openings);
            setBlocks(bl);
        } catch (e) {
            toast.error("Could not load availability settings.");
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        load();
    }, [load]);

    const addTimeToDay = (day) => {
        const t = timeInputs[day];
        if (!t) return;
        setDays((d) => ({ ...d, [day]: [...new Set([...(d[day] || []), t])].sort() }));
        setTimeInputs((ti) => ({ ...ti, [day]: "" }));
    };

    const removeTimeFromDay = (day, t) => {
        setDays((d) => ({ ...d, [day]: (d[day] || []).filter((x) => x !== t) }));
    };

    const saveSchedule = async () => {
        setSaving(true);
        try {
            const r = await adminSaveSchedule(token, days);
            setDays(r.schedule);
            toast.success("Weekly hours saved — your booking calendar is updated.");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not save weekly hours.");
        } finally {
            setSaving(false);
        }
    };

    const addOpening = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await adminAddOpening(token, openingForm);
            toast.success("Extra slot opened — it's bookable on the site now.");
            setOpeningForm({ date: "", time: "" });
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not open slot.");
        } finally {
            setSaving(false);
        }
    };

    const removeOpening = async (id) => {
        try {
            await adminDeleteOpening(token, id);
            toast.success("Opening removed.");
            load();
        } catch (e) {
            toast.error("Could not remove opening.");
        }
    };

    const addBlock = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await adminAddBlock(token, blockForm);
            toast.success("Slot blocked.");
            setBlockForm({ date: "", time: "" });
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not block slot.");
        } finally {
            setSaving(false);
        }
    };

    const removeBlock = async (id) => {
        try {
            await adminDeleteBlock(token, id);
            toast.success("Block removed.");
            load();
        } catch (e) {
            toast.error("Could not remove block.");
        }
    };

    if (loading) {
        return (
            <div className="flex items-center gap-2 text-ink/50 py-10" data-testid="availability-loading">
                <Loader2 className="animate-spin" size={16} /> Loading availability…
            </div>
        );
    }

    return (
        <div className="grid lg:grid-cols-2 gap-8" data-testid="admin-availability-tab">
            <section className="border border-ink/10 bg-white self-start" data-testid="admin-schedule-panel">
                <div className="px-6 py-4 border-b border-ink/10">
                    <h2 className="font-display font-medium text-xl flex items-center gap-2">
                        <Clock size={18} className="text-worx" /> Weekly hours
                    </h2>
                    <p className="text-xs text-ink/50 mt-1">
                        Your normal recurring schedule. Add one-hour start times to each day — leave a day empty to stay closed.
                    </p>
                </div>
                <div className="divide-y divide-ink/10">
                    {DAYS.map((name, i) => (
                        <div key={i} className="px-6 py-4" data-testid={`schedule-day-${i}`}>
                            <div className="flex items-center justify-between">
                                <span className="font-medium text-sm">{name}</span>
                                {(days?.[String(i)] || []).length === 0 && (
                                    <span className="text-xs text-ink/40" data-testid={`schedule-closed-${i}`}>Closed</span>
                                )}
                            </div>
                            <div className="mt-2 flex flex-wrap items-center gap-2">
                                {(days?.[String(i)] || []).map((t) => (
                                    <span
                                        key={t}
                                        data-testid={`schedule-chip-${i}-${t.replace(":", "")}`}
                                        className="inline-flex items-center gap-1.5 border border-ink/15 px-2.5 py-1 text-sm"
                                    >
                                        {fmtTime(t)}
                                        <button
                                            type="button"
                                            data-testid={`schedule-remove-${i}-${t.replace(":", "")}`}
                                            onClick={() => removeTimeFromDay(String(i), t)}
                                            className="text-ink/40 hover:text-worx transition-colors"
                                            title="Remove time"
                                        >
                                            <X size={12} />
                                        </button>
                                    </span>
                                ))}
                                <span className="inline-flex items-center gap-1.5">
                                    <Input
                                        type="time"
                                        aria-label={`Add ${name} time`}
                                        data-testid={`schedule-time-input-${i}`}
                                        value={timeInputs[String(i)] || ""}
                                        onChange={(e) => setTimeInputs((ti) => ({ ...ti, [String(i)]: e.target.value }))}
                                        className="h-8 w-28 rounded-none text-sm"
                                    />
                                    <button
                                        type="button"
                                        data-testid={`schedule-add-${i}`}
                                        onClick={() => addTimeToDay(String(i))}
                                        disabled={!timeInputs[String(i)]}
                                        className="inline-flex items-center justify-center h-8 w-8 border border-ink/15 hover:border-worx hover:text-worx transition-colors disabled:opacity-40"
                                        title={`Add time to ${name}`}
                                    >
                                        <Plus size={14} />
                                    </button>
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
                <div className="px-6 py-4 border-t border-ink/10">
                    <Button
                        data-testid="schedule-save-btn"
                        onClick={saveSchedule}
                        disabled={saving}
                        className="w-full bg-worx hover:bg-ink text-white rounded-none py-5"
                    >
                        {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
                        Save weekly hours
                    </Button>
                </div>
            </section>

            <div className="space-y-8">
                <section className="border border-ink/10 bg-white" data-testid="admin-openings-panel">
                    <div className="px-6 py-4 border-b border-ink/10">
                        <h2 className="font-display font-medium text-xl flex items-center gap-2">
                            <CalendarPlus size={18} className="text-worx" /> Open extra slots
                        </h2>
                        <p className="text-xs text-ink/50 mt-1">
                            One-off openings beyond your weekly hours — a Friday afternoon, an extra evening, anything.
                        </p>
                    </div>
                    <form onSubmit={addOpening} className="p-6 flex items-end gap-3">
                        <div className="flex-1">
                            <Label htmlFor="opening-date">Date</Label>
                            <Input id="opening-date" data-testid="opening-date-input" type="date" value={openingForm.date} onChange={(e) => setOpeningForm({ ...openingForm, date: e.target.value })} required className="mt-1.5 rounded-none" />
                        </div>
                        <div className="w-28">
                            <Label htmlFor="opening-time">Time</Label>
                            <Input id="opening-time" data-testid="opening-time-input" type="time" value={openingForm.time} onChange={(e) => setOpeningForm({ ...openingForm, time: e.target.value })} required className="mt-1.5 rounded-none" />
                        </div>
                        <Button data-testid="opening-add-btn" type="submit" disabled={saving} className="bg-worx hover:bg-ink text-white rounded-none">
                            <Plus size={16} />
                        </Button>
                    </form>
                    {openings.length > 0 && (
                        <ul data-testid="openings-list" className="divide-y divide-ink/10 border-t border-ink/10">
                            {openings.map((o) => (
                                <li key={o.opening_id} className="px-6 py-3 flex items-center justify-between text-sm" data-testid={`opening-${o.opening_id.slice(0, 8)}`}>
                                    <span>{fmtDate(o.date)} · {fmtTime(o.time)}</span>
                                    <button data-testid={`opening-remove-${o.opening_id.slice(0, 8)}`} onClick={() => removeOpening(o.opening_id)} className="text-ink/40 hover:text-worx transition-colors p-1" title="Remove opening">
                                        <Trash2 size={14} />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>

                <section className="border border-ink/10 bg-white" data-testid="admin-blocks-panel">
                    <div className="px-6 py-4 border-b border-ink/10">
                        <h2 className="font-display font-medium text-xl flex items-center gap-2">
                            <Ban size={18} className="text-worx" /> Block out time
                        </h2>
                        <p className="text-xs text-ink/50 mt-1">
                            Close a specific slot that would normally be bookable — appointments, errands, time off.
                        </p>
                    </div>
                    <form onSubmit={addBlock} className="p-6 flex items-end gap-3">
                        <div className="flex-1">
                            <Label htmlFor="block-date">Date</Label>
                            <Input id="block-date" data-testid="block-date-input" type="date" value={blockForm.date} onChange={(e) => setBlockForm({ ...blockForm, date: e.target.value })} required className="mt-1.5 rounded-none" />
                        </div>
                        <div className="w-28">
                            <Label htmlFor="block-time">Time</Label>
                            <Input id="block-time" data-testid="block-time-input" type="time" value={blockForm.time} onChange={(e) => setBlockForm({ ...blockForm, time: e.target.value })} required className="mt-1.5 rounded-none" />
                        </div>
                        <Button data-testid="block-add-btn" type="submit" disabled={saving} className="bg-ink hover:bg-worx text-paper rounded-none">
                            <Plus size={16} />
                        </Button>
                    </form>
                    {blocks.length > 0 && (
                        <ul data-testid="blocks-list" className="divide-y divide-ink/10 border-t border-ink/10">
                            {blocks.map((bl) => (
                                <li key={bl.block_id} className="px-6 py-3 flex items-center justify-between text-sm" data-testid={`block-${bl.block_id.slice(0, 8)}`}>
                                    <span>{fmtDate(bl.date)} · {fmtTime(bl.time)}</span>
                                    <button data-testid={`block-remove-${bl.block_id.slice(0, 8)}`} onClick={() => removeBlock(bl.block_id)} className="text-ink/40 hover:text-worx transition-colors p-1" title="Remove block">
                                        <Trash2 size={14} />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>
            </div>
        </div>
    );
};
